import { useState, useEffect } from'react'
import { Card } from'../components/ui/Card'
import { Button } from'../components/ui/Button'
import { 
 DollarSign, TrendingUp, Activity, Download,
 RefreshCw, PieChart, Users, Shield
} from'lucide-react'
import { format } from'date-fns'

interface BillingSummary {
 total_cost: number
 total_tokens: number
 total_input_tokens: number
 total_output_tokens: number
 total_requests: number
 unique_users: number
 average_cost_per_request: number
 average_tokens_per_request: number
 average_cost_per_user: number
}

interface ServiceStats {
 cost: number
 tokens: number
 input_tokens: number
 output_tokens: number
 requests: number
}

interface ModelStats {
 service: string
 model: string
 cost: number
 tokens: number
 input_tokens: number
 output_tokens: number
 requests: number
}

interface DailyBreakdown {
 date: string
 cost: number
 tokens: number
 input_tokens: number
 output_tokens: number
 requests: number
}

interface UserStats {
 user_id: number
 username: string
 email: string
 display_name: string
 full_name: string
 cost: number
 tokens: number
 input_tokens: number
 output_tokens: number
 requests: number
}

interface BillingData {
 success: boolean
 period: string
 start_date: string
 end_date: string
 summary: BillingSummary
 by_service: Record<string, ServiceStats>
 by_model: ModelStats[]
 by_operation: Record<string, ServiceStats>
 daily_breakdown: DailyBreakdown[]
}

interface UserBreakdownData {
 success: boolean
 period: string
 start_date: string
 end_date: string
 total_users: number
 users: UserStats[]
}

type PeriodType ='day' |'week' |'month' |'year'
type ViewType ='personal' |'admin'

export function Billing() {
 const [loading, setLoading] = useState(true)
 const [data, setData] = useState<BillingData | null>(null)
 const [userBreakdown, setUserBreakdown] = useState<UserBreakdownData | null>(null)
 const [period, setPeriod] = useState<PeriodType>('month')
 const [viewType, setViewType] = useState<ViewType>('personal')

 const fetchBillingData = async (selectedPeriod: PeriodType, selectedView: ViewType) => {
 try {
 setLoading(true)
 
 if (selectedView ==='admin') {
 // Fetch admin billing summary
 const summaryResponse = await fetch(`http://localhost:5001/api/billing/admin/summary?period=${selectedPeriod}`, {
 credentials:'include'
 })
 
 if (!summaryResponse.ok) {
 throw new Error('Failed to fetch billing data')
 }
 
 const summaryResult = await summaryResponse.json()
 setData(summaryResult)
 
 // Fetch ALL users breakdown (limit=1000 to get all users)
 const userResponse = await fetch(`http://localhost:5001/api/billing/admin/users?period=${selectedPeriod}&limit=1000`, {
 credentials:'include'
 })
 
 if (userResponse.ok) {
 const userResult = await userResponse.json()
 setUserBreakdown(userResult)
 }
 } else {
 // Fetch personal billing summary
 const summaryResponse = await fetch(`http://localhost:5001/api/billing/summary?period=${selectedPeriod}`, {
 credentials:'include'
 })
 
 if (!summaryResponse.ok) {
 throw new Error('Failed to fetch billing data')
 }
 
 const summaryResult = await summaryResponse.json()
 setData(summaryResult)
 setUserBreakdown(null) // Clear user breakdown in personal view
 }
 } catch (error) {
 console.error('Error fetching billing data:', error)
 } finally {
 setLoading(false)
 }
 }

 useEffect(() => {
 fetchBillingData(period, viewType)
 }, [period, viewType])

 const handleExport = async () => {
 try {
 const endpoint = viewType ==='admin' 
 ? `http://localhost:5001/api/billing/admin/export?period=${period}`
 : `http://localhost:5001/api/billing/export?period=${period}`
 
 const response = await fetch(endpoint, { credentials:'include' })
 
 if (!response.ok) {
 throw new Error('Failed to export billing data')
 }
 
 const blob = await response.blob()
 const url = window.URL.createObjectURL(blob)
 const a = document.createElement('a')
 a.href = url
 const prefix = viewType ==='admin' ?'admin_billing' :'my_billing'
 a.download = `${prefix}_export_${format(new Date(),'yyyy-MM-dd')}.csv`
 document.body.appendChild(a)
 a.click()
 window.URL.revokeObjectURL(url)
 document.body.removeChild(a)
 } catch (error) {
 console.error('Error exporting billing data:', error)
 }
 }

 const formatCurrency = (amount: number) => {
 return new Intl.NumberFormat('en-US', {
 style:'currency',
 currency:'USD',
 minimumFractionDigits: 2,
 maximumFractionDigits: 6
 }).format(amount)
 }

 const formatNumber = (num: number) => {
 return new Intl.NumberFormat('en-US').format(num)
 }

 if (loading) {
 return (
 <div className="min-h-screen bg-default p-8">
 <div className="max-w-7xl mx-auto">
 <div className="flex items-center justify-center h-64">
 <RefreshCw className="w-8 h-8 text-brand animate-spin" />
 </div>
 </div>
 </div>
 )
 }

 if (!data || !data.success) {
 return (
 <div className="min-h-screen bg-default p-8">
 <div className="max-w-7xl mx-auto">
 <Card className="p-8 text-center">
 <p className="text-secondary">Failed to load billing data. Please try again.</p>
 <Button
 onClick={() => fetchBillingData(period, viewType)}
 className="mt-4"
 >
 Retry
 </Button>
 </Card>
 </div>
 </div>
 )
 }

 return (
 <div className="min-h-screen bg-default p-8">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center justify-between mb-4">
 <div>
 <div className="flex items-center gap-3 mb-2">
 {viewType ==='admin' ? (
 <Shield className="w-8 h-8 text-brand" />
 ) : (
 <DollarSign className="w-8 h-8 text-brand" />
 )}
 <h1 className="text-2xl font-semibold text-primary">
 {viewType ==='admin' ?'Admin: System-Wide Billing' :'My Billing'}
 </h1>
 </div>
 <p className="text-secondary">
 {viewType ==='admin' 
 ?'Track AI consumption and costs across all users (Admin View)'
 :'Track your personal AI consumption and costs'}
 </p>
 </div>
 <div className="flex items-center gap-4">
 {/* View Type Selector */}
 <div className="flex bg-surface rounded-lg shadow-sm border border-soft">
 <button
 onClick={() => setViewType('personal')}
 className={`px-4 py-2 text-sm font-medium transition-colors rounded-l-lg ${
viewType ==='personal'
?'bg-brand text-on-brand'
 :'bg-surface text-secondary hover:bg-surface-alt'
 }`}
 >
 My Billing
 </button>
 <button
 onClick={() => setViewType('admin')}
 className={`px-4 py-2 text-sm font-medium transition-colors rounded-r-lg ${
viewType ==='admin'
?'bg-info text-on-brand'
 :'bg-surface text-secondary hover:bg-surface-alt'
 }`}
 >
 Admin View
 </button>
 </div>

 {/* Period Selector */}
 <div className="flex bg-surface rounded-lg shadow-sm border border-soft">
 {(['day','week','month','year'] as PeriodType[]).map((p) => (
 <button
 key={p}
 onClick={() => setPeriod(p)}
 className={`px-4 py-2 text-sm font-medium transition-colors ${
period === p
?'bg-brand text-on-brand'
 :'bg-surface text-secondary hover:bg-surface-alt'
 } ${p ==='day' ?'rounded-l-lg' :''} ${p ==='year' ?'rounded-r-lg' :''}`}
 >
 {p.charAt(0).toUpperCase() + p.slice(1)}
 </button>
 ))}
 </div>
 
 {/* Export Button */}
 <div className="relative">
 <Button
 onClick={handleExport}
 variant="outline"
 className="flex items-center gap-2"
 >
 <Download className="w-4 h-4" />
 Export CSV
 </Button>
 </div>
 
 {/* Refresh Button */}
 <Button
 onClick={() => fetchBillingData(period, viewType)}
 variant="outline"
 className="flex items-center gap-2"
 >
 <RefreshCw className="w-4 h-4" />
 Refresh
 </Button>
 </div>
 </div>
 
 <div className="text-sm text-muted">
 {format(new Date(data.start_date),'MMM dd, yyyy')} - {format(new Date(data.end_date),'MMM dd, yyyy')}
 </div>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
 <Card className="p-6 bg-brand text-on-brand">
 <div className="flex items-center justify-between mb-2">
 <DollarSign className="w-8 h-8" />
 <span className="text-sm opacity-90">Total Cost</span>
 </div>
 <div className="text-2xl font-semibold">
 {formatCurrency(data.summary.total_cost)}
 </div>
 <div className="text-sm opacity-90 mt-1">
 {formatCurrency(data.summary.average_cost_per_request)} per request
 </div>
 </Card>

 <Card className="p-6 bg-brand text-on-brand">
 <div className="flex items-center justify-between mb-2">
 <Users className="w-8 h-8" />
 <span className="text-sm opacity-90">Active Users</span>
 </div>
 <div className="text-2xl font-semibold">
 {formatNumber(data.summary.unique_users || (viewType ==='personal' ? 1 : 0))}
 </div>
 <div className="text-sm opacity-90 mt-1">
 {viewType ==='personal' 
 ? formatCurrency(data.summary.total_cost) +' (you)' 
 : formatCurrency(data.summary.average_cost_per_user || 0) +' per user'}
 </div>
 </Card>

 <Card className="p-6 bg-ok text-on-brand">
 <div className="flex items-center justify-between mb-2">
 <Activity className="w-8 h-8" />
 <span className="text-sm opacity-90">Total Tokens</span>
 </div>
 <div className="text-2xl font-semibold">
 {formatNumber(data.summary.total_tokens)}
 </div>
 <div className="text-sm opacity-90 mt-1">
 {formatNumber(Math.round(data.summary.average_tokens_per_request))} per request
 </div>
 </Card>

 <Card className="p-6 bg-info text-on-brand">
 <div className="flex items-center justify-between mb-2">
 <TrendingUp className="w-8 h-8" />
 <span className="text-sm opacity-90">Requests</span>
 </div>
 <div className="text-2xl font-semibold">
 {formatNumber(data.summary.total_requests)}
 </div>
 <div className="text-sm opacity-90 mt-1">
 {period ==='day' ?'Today' : 
 period ==='week' ?'This Week' :
 period ==='month' ?'This Month' :'This Year'}
 </div>
 </Card>

 <Card className="p-6 bg-warn text-on-brand">
 <div className="flex items-center justify-between mb-2">
 <PieChart className="w-8 h-8" />
 <span className="text-sm opacity-90">Token Split</span>
 </div>
 <div className="text-xl font-bold">
 {formatNumber(data.summary.total_input_tokens)} / {formatNumber(data.summary.total_output_tokens)}
 </div>
 <div className="text-sm opacity-90 mt-1">
 Input / Output
 </div>
 </Card>
 </div>

 {/* Charts and Tables */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
 {/* By Service */}
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4">By Service</h3>
 <div className="space-y-4">
 {Object.entries(data.by_service).map(([service, stats]) => (
 <div key={service} className="flex items-center justify-between">
 <div className="flex-1">
 <div className="flex items-center justify-between mb-1">
 <span className="font-medium text-primary capitalize">{service}</span>
 <span className="text-sm text-secondary">{formatCurrency(stats.cost)}</span>
 </div>
 <div className="flex items-center gap-2 text-xs text-muted">
 <span>{formatNumber(stats.tokens)} tokens</span>
 <span>•</span>
 <span>{formatNumber(stats.requests)} requests</span>
 </div>
 </div>
 </div>
 ))}
 </div>
 </Card>

 {/* By Model */}
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4">By Model</h3>
 <div className="space-y-4 max-h-[300px] overflow-y-auto">
 {data.by_model.map((model, idx) => (
 <div key={idx} className="flex items-center justify-between">
 <div className="flex-1">
 <div className="flex items-center justify-between mb-1">
 <span className="font-medium text-primary text-sm">{model.model}</span>
 <span className="text-sm text-secondary">{formatCurrency(model.cost)}</span>
 </div>
 <div className="flex items-center gap-2 text-xs text-muted">
 <span className="capitalize">{model.service}</span>
 <span>•</span>
 <span>{formatNumber(model.tokens)} tokens</span>
 <span>•</span>
 <span>{formatNumber(model.requests)} requests</span>
 </div>
 </div>
 </div>
 ))}
 </div>
 </Card>
 </div>

 {/* Daily Breakdown */}
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4">Daily Breakdown</h3>
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-soft">
 <thead className="bg-surface-alt">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Date
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Cost
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Tokens
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Input
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Output
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Requests
 </th>
 </tr>
 </thead>
 <tbody className="bg-surface divide-y divide-soft">
 {data.daily_breakdown.map((day) => (
 <tr key={day.date} className="hover:bg-surface-alt">
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
 {format(new Date(day.date),'MMM dd, yyyy')}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right">
 {formatCurrency(day.cost)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right">
 {formatNumber(day.tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-muted text-right">
 {formatNumber(day.input_tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-muted text-right">
 {formatNumber(day.output_tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right">
 {formatNumber(day.requests)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </Card>

 {/* All Users Consumption - Only show in admin view */}
 {viewType ==='admin' && userBreakdown && userBreakdown.users && userBreakdown.users.length > 0 && (
 <Card className="p-6 mt-6">
 <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <Users className="w-5 h-5 text-brand" />
 User Consumption Breakdown
 </h3>
 <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
 <table className="min-w-full divide-y divide-soft">
 <thead className="bg-surface-alt sticky top-0">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Rank
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 User
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Cost
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 % of Total
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Tokens
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Input
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Output
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Requests
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
 Avg Cost/Req
 </th>
 </tr>
 </thead>
 <tbody className="bg-surface divide-y divide-soft">
 {userBreakdown.users.map((user, index) => {
 const percentOfTotal = data ? (user.cost / data.summary.total_cost * 100) : 0
 const avgCostPerRequest = user.requests > 0 ? user.cost / user.requests : 0
 
 return (
 <tr key={user.user_id} className="hover:bg-surface-alt">
 <td className="px-6 py-4 whitespace-nowrap text-sm text-muted">
 #{index + 1}
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex flex-col">
 <span className="text-sm font-medium text-primary">{user.display_name}</span>
 <span className="text-xs text-muted">{user.email}</span>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right font-semibold">
 {formatCurrency(user.cost)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary text-right">
 {percentOfTotal.toFixed(1)}%
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right">
 {formatNumber(user.tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-muted text-right">
 {formatNumber(user.input_tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-muted text-right">
 {formatNumber(user.output_tokens)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-primary text-right">
 {formatNumber(user.requests)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary text-right">
 {formatCurrency(avgCostPerRequest)}
 </td>
 </tr>
 )
 })}
 </tbody>
 </table>
 </div>
 <div className="mt-4 text-sm text-secondary flex items-center justify-between">
 <span>Showing all {userBreakdown.users.length} active users (sorted by cost, highest first)</span>
 <span className="font-medium">Total: {userBreakdown.total_users} users with activity</span>
 </div>
 </Card>
 )}

 {/* By Operation */}
 <Card className="p-6 mt-6">
 <h3 className="text-lg font-semibold text-primary mb-4">By Operation</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {Object.entries(data.by_operation).map(([operation, stats]) => (
 <div key={operation} className="p-4 bg-surface-alt rounded-lg">
 <div className="font-medium text-primary mb-2">{operation}</div>
 <div className="text-sm text-secondary space-y-1">
 <div className="flex justify-between">
 <span>Cost:</span>
 <span className="font-medium">{formatCurrency(stats.cost)}</span>
 </div>
 <div className="flex justify-between">
 <span>Tokens:</span>
 <span className="font-medium">{formatNumber(stats.tokens)}</span>
 </div>
 <div className="flex justify-between">
 <span>Requests:</span>
 <span className="font-medium">{formatNumber(stats.requests)}</span>
 </div>
 </div>
 </div>
 ))}
 </div>
 </Card>
 </div>
 </div>
 )
}
